import { Unit } from "../model";
import { CalcRowsLogic } from "../logic/CalcRowsLogic";
import { Translations } from "../logic/Translations";
/**
 * Helpers for handling numbers: Mathematical operations, parsing and formatting numbers in our supported languages etc.
 */
export class Numeric {

  /**
   * Parses a user input to a float taking into consideration the Finnish culture.
   * Main case is that decimal separator is usually a comma (,).
   * If there is both comma (,) and point (.) in the same string, NaN is returned.
   * Whitespaces are removed and if string contains commas they are replaced with points before conversion to Number.
   * Null, undefined return original value, empty strings (or whitespace only) return null.
   *
   * @param numCandidate - text that should be converted to a number if possible.
   */
  public static parseNumber(numCandidate: string | number): number | undefined {
    if (numCandidate == null || typeof (numCandidate) === "number") {
      return numCandidate as number;
    }
    if (typeof (numCandidate) === "string") {
      if (numCandidate.indexOf(",") >= 0 && numCandidate.indexOf(".") >= 0) {
        return Number.NaN;
      }
      numCandidate = numCandidate.replace(/\s/, "");
      if (numCandidate === "") {
        return undefined;
      }
      return Number(numCandidate.replace(",", "."));
    }
    return Number(numCandidate);
  }

  /**
   * Formats a number as a euro price. Uses comma as decimal separator.
   * @param value - Numeric value to format
   * @param addSuffix - If true (default), adds the €-suffix.
   * @param nullChar The character or string for null or Nan value, the default is '-'.
   */
  public static formatPrice(value: any,  addSuffix = true, nullChar = "-" ): string {
    // TODO: Add localization for English language (dot instead of comma as decimal separator).
    // TODO: Add thousand separator?

    return Numeric.formatNumber(value, 2, (addSuffix ? "€" : ""), nullChar );
  }

  /**
   * Rounds a JavaScript number in a reliable way.
   * @param value Number that is rounded. If null given, returns null.
   * @param decimals Number of decimals after the decimal separator. Default is 2 as in money.
   * @returns Number properly rounded, Null/undefined as null, Non-numeric values return Number.NaN
   */
  public static round(value: number | undefined | null, decimals: number | undefined = 2): number | undefined {
    if (value == undefined || decimals == undefined) { // '==' handles both null and undefined values, do not use here '==='
      return undefined;
    }
    if (value < 0) {
      // Round away from zero
      return -1 * Numeric.round(-1 * value, decimals)!;
    }
    let sValue = `${value}`;
    if (sValue.indexOf("e") >= 0) {
      sValue = `${value.toFixed(15)}`;
    }
    return Number(Math.round(`${sValue}e${decimals}` as any).toString() + `e-${decimals}`);
  }

  /**
   * Fixes floating point problems in typical number.toString() scenarios.
   * E.g. `console.debug((0.07 * 100).toString());` returns "7.000000000000001".
   * Null/undefined returns empty string, numbers fixed and otherwise toString() is called.
   * @param value Number to show as string.
   * @returns Number rounded to specified number of decimals. Null/undefined returns empty string.
   * @example
   * console.debug(Numeric.toString(0.07 * 100)); // returns "7"
   * console.debug((0.07 * 100).toString());      // returns "7.000000000000001"
   */
  public static toString(value: number): string {
    if (value == null) {
      return "";
    }
    return value.toFixed ? parseFloat(value.toFixed(12)).toString() : value.toString() ;
  }

  /**
   * Fixes floating point problems in typical number.toFixed() scenarios.
   * E.g. `console.debug((1.005).toFixed(2));` returns "1.00"
   * @param value Number to show as string. Supports null/undefined, but otherwise should be a number.
   * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
   * @returns Number rounded to specified number of decimals. Null/undefined returns empty string.
   * @example
   * console.debug(Numeric.toFixed(1.005, 2)) // returns "1.01"
   * console.debug((1.005).toFixed(2))        // returns 1.00
   */
  public static toFixed(value: number | undefined, fractionDigits = 0): string {
    value = Numeric.round(value, fractionDigits);
    return value == null ? "" : value.toFixed(fractionDigits);
  }

  /**
   * Rounds a JavaScript number in a reliable way in percentage format.
   *
   * @param value Number that is rounded.
   * @param decimals Number of decimals after the decimal separator. Default is 2 as in money.
   * If decimals is null, no rounding is done.
   * @param addSuffix If true, adds '%' sign at the end.
   * @param nullChar The character or string for null or Nan value, the default is '-'.
   */
  public static formatPercent(value: any, decimals = 2, addSuffix = true, nullChar = "-"): string {
    return Numeric.formatNumber(value, decimals, (addSuffix ? "%" : ""), nullChar  );
  }

  /**
   * Rounds a JavaScript number and formats it using comma as a separator and optional suffix.
   *
   * @param value Number that is rounded.
   * @param decimals Number of decimals after the decimal separator. Default is 2 as in money. If decimals is null, no rounding is done.
   * @param suffix The suffix to add to the number.
   * @param nullChar The character or string for null or Nan value, the default is '-'.
   */
  public static formatNumber(value: any, decimals = 2, suffix = "",  nullChar = "-"): string {
    const asFloat = parseFloat(value);
    if (isNaN(asFloat)) { return nullChar; }
    let asFixed = Numeric.toFixed(asFloat, decimals == null ? 10 : decimals);
    if (decimals == null) {
        asFixed = `${parseFloat(asFixed)}`;
    }
    return asFixed.replace(/\./, ",") + suffix;
  }

  /**
   * Formats a number as count optionally taking into account the related unit
   * @param input Input to format
   * @param unit Unit that is used in formatting
   * @param decimals Number of decimals to show
   * @param nullText Text to show if the value is null / undefined. By default returns null, which can then be cought by another filter or logical or ("||")
   * @param zeroText If specified, will use this text without the Unit formatting if the value is zero. By default zero is formatted as "0 [unitText]"
   * @returns formatted input
   */
  public static formatUnit(input: number,
    unit: Unit | undefined = undefined,
    decimals: number | undefined = undefined,
    nullText: string | undefined = undefined,
    zeroText: string| undefined = undefined): string | undefined {
      if (input == null) {
        return nullText;
      }
      if (zeroText != null && input === 0) {
        return zeroText;
      }
      const rounded = Numeric.round(input, decimals || 2);
      switch (unit) {
        case Unit.One:
          return "";
        case Unit.Period:
          return rounded + Translations.get("SALAXY.NG1.FilterFunctions.sxyCount.period" + (rounded === 1 ? "" : "s"));
        case Unit.Percent:
          return Numeric.round(input * 100, decimals || 2) + "%";
        case Unit.Hours:
        case Unit.Days:
        case Unit.Weeks:
        case Unit.Count:
        case Unit.Kilometers:
        case Unit.Undefined:
        default:
          return rounded + " " + (CalcRowsLogic.getUnitIndicator(unit) || "");
      }
    }

  /**
   * Checks if the given object is a number.
   * @param numberCandidate Object to check
   */
  public static isNumber(numberCandidate: any): boolean {
    return !isNaN(parseFloat(numberCandidate)) && isFinite(numberCandidate);
  }

}
